import { useEffect, useMemo, useState } from 'react';
import {
  explainProcessorRule,
  mergeValidationReports,
  validateProcessorDraft,
} from '../../lib/nodeValidation';
import type { NodeConnections, ProcessorRuleConfig } from '../../types/nodeConfig';
import type { ValidationReport } from '../../types/validation';
import NodeValidationPanel from './NodeValidationPanel';
import SearchableRouteSelect from './SearchableRouteSelect';

interface Props {
  node: Record<string, unknown>;
  connections: NodeConnections;
  productTypeOptions: Array<{ id: string; name: string }>;
  serverValidation?: ValidationReport | null;
  readOnly?: boolean;
  onChange: (patch: Record<string, unknown>) => void;
}

export default function ProcessorNodeConfig({
  node,
  connections,
  productTypeOptions,
  serverValidation,
  readOnly,
  onChange,
}: Props) {
  const rules = (node.processor_rules as ProcessorRuleConfig[] | undefined) ?? [];
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(
    rules[0]?.id ?? null,
  );

  useEffect(() => {
    if (!selectedRuleId && rules[0]) {
      setSelectedRuleId(rules[0].id);
    } else if (
      selectedRuleId &&
      rules.length > 0 &&
      !rules.some((r) => r.id === selectedRuleId)
    ) {
      setSelectedRuleId(rules[0].id);
    }
  }, [rules, selectedRuleId]);

  const selectedRule =
    rules.find((r) => r.id === selectedRuleId) ?? rules[0] ?? null;

  const productNames = useMemo(
    () => new Map(productTypeOptions.map((p) => [p.id, p.name])),
    [productTypeOptions],
  );

  const localValidation = useMemo(
    () => validateProcessorDraft(node, connections.incoming, connections.outgoing),
    [node, connections],
  );

  const combinedValidation = useMemo(
    () => mergeValidationReports(serverValidation, localValidation),
    [serverValidation, localValidation],
  );

  const updateRules = (next: ProcessorRuleConfig[]) =>
    onChange({ processor_rules: next });

  const selectRule = (ruleId: string) => setSelectedRuleId(ruleId);

  const updateSelectedRule = (patch: Partial<ProcessorRuleConfig>) => {
    if (!selectedRule) return;
    updateRules(
      rules.map((r) => (r.id === selectedRule.id ? { ...r, ...patch } : r)),
    );
  };

  const moveRule = (index: number, direction: -1 | 1) => {
    const next = [...rules];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    updateRules(
      next.map((r, i) => ({ ...r, priority: i })),
    );
  };

  const addRule = () => {
    const rule: ProcessorRuleConfig = {
      id: crypto.randomUUID(),
      name: `Rule ${rules.length + 1}`,
      enabled: true,
      priority: rules.length,
      logic_mode: 'and',
      input_requirements: [],
      output_specs: [],
    };
    updateRules([...rules, rule]);
    selectRule(rule.id);
  };

  const ruleExplanation = selectedRule
    ? explainProcessorRule(
        selectedRule,
        connections.incoming,
        connections.outgoing,
        productNames,
      )
    : null;

  return (
    <section className="space-y-4 rounded-lg border border-border bg-surface/40 p-4">
      <h3 className="panel-section-title">Processor configuration</h3>

      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs text-text-muted">Capacity</label>
          <input
            type="number"
            min={1}
            className="input-field w-full"
            disabled={readOnly}
            value={Number(node.capacity ?? 1)}
            onChange={(e) => onChange({ capacity: Number(e.target.value) })}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-text-muted">
            Queue warning threshold
          </label>
          <input
            type="number"
            min={0}
            className="input-field w-full"
            disabled={readOnly}
            value={Number(node.queue_warning_threshold ?? 5)}
            onChange={(e) =>
              onChange({ queue_warning_threshold: Number(e.target.value) })
            }
          />
        </div>
        <label className="flex items-end gap-2 pb-2 text-sm text-text-muted">
          <input
            type="checkbox"
            disabled={readOnly}
            checked={node.runtime_congestion_warning_enabled !== false}
            onChange={(e) =>
              onChange({
                runtime_congestion_warning_enabled: e.target.checked,
              })
            }
          />
          Runtime congestion warnings
        </label>
      </div>

      <div className="grid gap-4 lg:grid-cols-[200px_1fr_1fr]">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-text-muted">Rules</p>
            {!readOnly && (
              <button type="button" className="btn-ghost px-1 text-xs" onClick={addRule}>
                + Add
              </button>
            )}
          </div>
          <ul className="space-y-1">
            {rules.map((rule, index) => (
              <li
                key={rule.id}
                className={`rounded-md border ${
                  selectedRule?.id === rule.id
                    ? 'border-accent/40 bg-accent/10'
                    : 'border-transparent'
                }`}
              >
                <button
                  type="button"
                  className="w-full rounded-md px-2 py-1.5 text-left text-xs text-text-muted hover:bg-surface-overlay"
                  onClick={() => selectRule(rule.id)}
                >
                  <span className={rule.enabled ? 'text-text' : 'text-text-subtle line-through'}>
                    {rule.name}
                  </span>
                  <span className="ml-1 text-text-subtle">P{rule.priority}</span>
                </button>
                {!readOnly && (
                  <div className="flex items-center gap-1 px-1 pb-1">
                    <button
                      type="button"
                      className="btn-ghost px-1 text-[10px]"
                      disabled={index === 0}
                      onClick={() => moveRule(index, -1)}
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="btn-ghost px-1 text-[10px]"
                      disabled={index === rules.length - 1}
                      onClick={() => moveRule(index, 1)}
                      title="Move down"
                    >
                      ↓
                    </button>
                    <label className="ml-auto flex items-center gap-1 text-[10px] text-text-subtle">
                      <input
                        type="checkbox"
                        checked={rule.enabled}
                        onChange={(e) => {
                          updateRules(
                            rules.map((r) =>
                              r.id === rule.id ? { ...r, enabled: e.target.checked } : r,
                            ),
                          );
                        }}
                      />
                      On
                    </label>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>

        {selectedRule ? (
          <>
            <div className="space-y-3 rounded-lg border border-border/80 bg-surface/30 p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-text-muted">Input requirements</p>
                <select
                  className="input-field w-24 py-1 text-xs"
                  disabled={readOnly}
                  value={selectedRule.logic_mode}
                  onChange={(e) =>
                    updateSelectedRule({
                      logic_mode: e.target.value as 'and' | 'or',
                    })
                  }
                >
                  <option value="and">AND</option>
                  <option value="or">OR</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <input
                  className="input-field col-span-2 text-xs"
                  disabled={readOnly}
                  value={selectedRule.name}
                  placeholder="Rule name"
                  onChange={(e) => updateSelectedRule({ name: e.target.value })}
                />
                <div>
                  <label className="mb-1 block text-[10px] text-text-subtle">Priority</label>
                  <input
                    type="number"
                    className="input-field w-full text-xs"
                    disabled={readOnly}
                    value={selectedRule.priority}
                    onChange={(e) =>
                      updateSelectedRule({ priority: Number(e.target.value) })
                    }
                  />
                </div>
                <label className="flex items-end gap-2 pb-1 text-xs text-text-muted">
                  <input
                    type="checkbox"
                    disabled={readOnly}
                    checked={selectedRule.enabled}
                    onChange={(e) =>
                      updateSelectedRule({ enabled: e.target.checked })
                    }
                  />
                  Enabled
                </label>
              </div>

              {selectedRule.input_requirements.map((req, index) => (
                <div
                  key={req.id}
                  className="space-y-2 rounded-md border border-border/50 p-2"
                >
                  <SearchableRouteSelect
                    routes={connections.incoming}
                    value={req.incoming_route_id}
                    direction="incoming"
                    disabled={readOnly}
                    onChange={(routeId) => {
                      const reqs = [...selectedRule.input_requirements];
                      reqs[index] = { ...req, incoming_route_id: routeId };
                      updateSelectedRule({ input_requirements: reqs });
                    }}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="mb-1 block text-[10px] text-text-subtle">Min count</label>
                      <input
                        type="number"
                        min={1}
                        className="input-field w-full text-xs"
                        disabled={readOnly}
                        value={req.min_count}
                        onChange={(e) => {
                          const reqs = [...selectedRule.input_requirements];
                          reqs[index] = {
                            ...req,
                            min_count: Number(e.target.value),
                          };
                          updateSelectedRule({ input_requirements: reqs });
                        }}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] text-text-subtle">Product</label>
                      <select
                        className="input-field w-full text-xs"
                        disabled={readOnly}
                        value={req.product_type_id}
                        onChange={(e) => {
                          const reqs = [...selectedRule.input_requirements];
                          reqs[index] = {
                            ...req,
                            product_type_id: e.target.value,
                          };
                          updateSelectedRule({ input_requirements: reqs });
                        }}
                      >
                        <option value="*">All products</option>
                        {productTypeOptions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {!readOnly && (
                    <button
                      type="button"
                      className="btn-danger-ghost text-[10px]"
                      onClick={() =>
                        updateSelectedRule({
                          input_requirements: selectedRule.input_requirements.filter(
                            (_, i) => i !== index,
                          ),
                        })
                      }
                    >
                      Remove requirement
                    </button>
                  )}
                </div>
              ))}

              {!readOnly && (
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() =>
                    updateSelectedRule({
                      input_requirements: [
                        ...selectedRule.input_requirements,
                        {
                          id: crypto.randomUUID(),
                          incoming_route_id:
                            connections.incoming[0]?.route_id ?? '',
                          min_count: 1,
                          product_type_id: '*',
                        },
                      ],
                    })
                  }
                >
                  Add requirement
                </button>
              )}
            </div>

            <div className="space-y-3 rounded-lg border border-border/80 bg-surface/30 p-3">
              <p className="text-xs font-medium text-text-muted">Output specs</p>

              {selectedRule.output_specs.map((spec, index) => (
                <div
                  key={spec.id}
                  className="space-y-2 rounded-md border border-border/50 p-2"
                >
                  <SearchableRouteSelect
                    routes={connections.outgoing}
                    value={spec.target_route_id}
                    direction="outgoing"
                    disabled={readOnly}
                    onChange={(routeId) => {
                      const specs = [...selectedRule.output_specs];
                      specs[index] = { ...spec, target_route_id: routeId };
                      updateSelectedRule({ output_specs: specs });
                    }}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="mb-1 block text-[10px] text-text-subtle">Emit count</label>
                      <input
                        type="number"
                        min={1}
                        className="input-field w-full text-xs"
                        disabled={readOnly}
                        value={spec.emit_count}
                        onChange={(e) => {
                          const specs = [...selectedRule.output_specs];
                          specs[index] = {
                            ...spec,
                            emit_count: Number(e.target.value),
                          };
                          updateSelectedRule({ output_specs: specs });
                        }}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] text-text-subtle">Delay (sec)</label>
                      <input
                        type="number"
                        min={0}
                        className="input-field w-full text-xs"
                        disabled={readOnly}
                        value={spec.processing_delay_seconds ?? 0}
                        onChange={(e) => {
                          const specs = [...selectedRule.output_specs];
                          specs[index] = {
                            ...spec,
                            processing_delay_seconds: Number(e.target.value),
                          };
                          updateSelectedRule({ output_specs: specs });
                        }}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] text-text-subtle">Binding mode</label>
                      <select
                        className="input-field w-full text-xs"
                        disabled={readOnly}
                        value={spec.binding_mode ?? 'none'}
                        onChange={(e) => {
                          const specs = [...selectedRule.output_specs];
                          specs[index] = {
                            ...spec,
                            binding_mode: e.target.value,
                            bound_input_requirement_id:
                              e.target.value === 'none'
                                ? undefined
                                : spec.bound_input_requirement_id,
                          };
                          updateSelectedRule({ output_specs: specs });
                        }}
                      >
                        <option value="none">None</option>
                        <option value="bound">Bound to input</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] text-text-subtle">Bound requirement</label>
                      <select
                        className="input-field w-full text-xs"
                        disabled={
                          readOnly || (spec.binding_mode ?? 'none') === 'none'
                        }
                        value={spec.bound_input_requirement_id ?? ''}
                        onChange={(e) => {
                          const specs = [...selectedRule.output_specs];
                          specs[index] = {
                            ...spec,
                            bound_input_requirement_id: e.target.value || undefined,
                          };
                          updateSelectedRule({ output_specs: specs });
                        }}
                      >
                        <option value="">Select requirement</option>
                        {selectedRule.input_requirements.map((req) => (
                          <option key={req.id} value={req.id}>
                            {req.id.slice(0, 8)}… (min {req.min_count})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] text-text-subtle">Schedule mode</label>
                      <select
                        className="input-field w-full text-xs"
                        disabled={readOnly}
                        value={spec.schedule_mode ?? 'immediate'}
                        onChange={(e) => {
                          const specs = [...selectedRule.output_specs];
                          const mode = e.target.value;
                          specs[index] = {
                            ...spec,
                            schedule_mode: mode,
                            schedule_entries:
                              mode === 'immediate'
                                ? []
                                : (spec.schedule_entries?.length
                                    ? spec.schedule_entries
                                    : [{ delay_minutes: 0 }]),
                          };
                          updateSelectedRule({ output_specs: specs });
                        }}
                      >
                        <option value="immediate">Immediate</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="delayed">Delayed</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] text-text-subtle">Output product</label>
                      <select
                        className="input-field w-full text-xs"
                        disabled={readOnly}
                        value={spec.output_product_type_id ?? ''}
                        onChange={(e) => {
                          const specs = [...selectedRule.output_specs];
                          specs[index] = {
                            ...spec,
                            output_product_type_id: e.target.value || undefined,
                          };
                          updateSelectedRule({ output_specs: specs });
                        }}
                      >
                        <option value="">Same as input</option>
                        {productTypeOptions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] text-text-subtle">Size multiplier</label>
                      <input
                        type="number"
                        min={0.01}
                        step={0.1}
                        className="input-field w-full text-xs"
                        disabled={readOnly}
                        value={spec.size_multiplier ?? 1}
                        onChange={(e) => {
                          const specs = [...selectedRule.output_specs];
                          specs[index] = {
                            ...spec,
                            size_multiplier: Number(e.target.value),
                          };
                          updateSelectedRule({ output_specs: specs });
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-[10px] text-text-subtle">
                      Trigger expression
                    </label>
                    <input
                      className="input-field w-full font-mono text-xs"
                      disabled={readOnly}
                      placeholder="e.g. (req_id and req_id) or req_id"
                      value={spec.trigger_expression ?? ''}
                      onChange={(e) => {
                        const specs = [...selectedRule.output_specs];
                        specs[index] = {
                          ...spec,
                          trigger_expression: e.target.value,
                        };
                        updateSelectedRule({ output_specs: specs });
                      }}
                    />
                    <p className="mt-1 text-[10px] text-text-subtle">
                      Refs:{' '}
                      {selectedRule.input_requirements.length > 0
                        ? selectedRule.input_requirements
                            .map((r) => r.id)
                            .join(', ')
                        : 'add input requirements first'}
                      . Empty or all_requirements_met = always emit.
                    </p>
                  </div>

                  {(spec.schedule_mode ?? 'immediate') !== 'immediate' && (
                    <div className="space-y-2 rounded border border-border/40 p-2">
                      <p className="text-[10px] font-medium text-text-subtle">
                        Schedule entries (delay min)
                      </p>
                      {(spec.schedule_entries ?? []).map((entry, entryIndex) => (
                        <div
                          key={entryIndex}
                          className="flex items-center gap-2"
                        >
                          <input
                            type="number"
                            min={0}
                            step={0.5}
                            className="input-field w-24 text-xs"
                            disabled={readOnly}
                            value={entry.delay_minutes}
                            onChange={(e) => {
                              const specs = [...selectedRule.output_specs];
                              const entries = [...(spec.schedule_entries ?? [])];
                              entries[entryIndex] = {
                                ...entry,
                                delay_minutes: Number(e.target.value),
                              };
                              specs[index] = { ...spec, schedule_entries: entries };
                              updateSelectedRule({ output_specs: specs });
                            }}
                          />
                          <span className="text-[10px] text-text-subtle">min</span>
                          {!readOnly && (
                            <button
                              type="button"
                              className="btn-danger-ghost text-[10px]"
                              onClick={() => {
                                const specs = [...selectedRule.output_specs];
                                specs[index] = {
                                  ...spec,
                                  schedule_entries: (spec.schedule_entries ?? []).filter(
                                    (_, i) => i !== entryIndex,
                                  ),
                                };
                                updateSelectedRule({ output_specs: specs });
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                      {!readOnly && (
                        <button
                          type="button"
                          className="btn-ghost text-[10px]"
                          onClick={() => {
                            const specs = [...selectedRule.output_specs];
                            specs[index] = {
                              ...spec,
                              schedule_entries: [
                                ...(spec.schedule_entries ?? []),
                                { delay_minutes: 0 },
                              ],
                            };
                            updateSelectedRule({ output_specs: specs });
                          }}
                        >
                          Add entry
                        </button>
                      )}
                    </div>
                  )}

                  <details className="text-xs text-text-muted" open>
                    <summary className="cursor-pointer text-text-subtle">Visual override</summary>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <input
                        type="color"
                        className="input-field h-8 p-0"
                        disabled={readOnly}
                        value={spec.visual_override?.color ?? '#ffffff'}
                        onChange={(e) => {
                          const specs = [...selectedRule.output_specs];
                          specs[index] = {
                            ...spec,
                            visual_override: {
                              ...spec.visual_override,
                              color: e.target.value,
                            },
                          };
                          updateSelectedRule({ output_specs: specs });
                        }}
                      />
                      <select
                        className="input-field text-xs"
                        disabled={readOnly}
                        value={spec.visual_override?.shape ?? ''}
                        onChange={(e) => {
                          const specs = [...selectedRule.output_specs];
                          specs[index] = {
                            ...spec,
                            visual_override: {
                              ...spec.visual_override,
                              shape: e.target.value || undefined,
                            },
                          };
                          updateSelectedRule({ output_specs: specs });
                        }}
                      >
                        <option value="">Default shape</option>
                        <option value="circle">Circle</option>
                        <option value="square">Square</option>
                        <option value="triangle">Triangle</option>
                        <option value="diamond">Diamond</option>
                      </select>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        className="input-field text-xs"
                        placeholder="Size px"
                        disabled={readOnly}
                        value={spec.visual_override?.size ?? ''}
                        onChange={(e) => {
                          const specs = [...selectedRule.output_specs];
                          specs[index] = {
                            ...spec,
                            visual_override: {
                              ...spec.visual_override,
                              size: e.target.value
                                ? Number(e.target.value)
                                : undefined,
                            },
                          };
                          updateSelectedRule({ output_specs: specs });
                        }}
                      />
                    </div>
                  </details>

                  {!readOnly && (
                    <button
                      type="button"
                      className="btn-danger-ghost text-[10px]"
                      onClick={() =>
                        updateSelectedRule({
                          output_specs: selectedRule.output_specs.filter(
                            (_, i) => i !== index,
                          ),
                        })
                      }
                    >
                      Remove output
                    </button>
                  )}
                </div>
              ))}

              {!readOnly && (
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() =>
                    updateSelectedRule({
                      output_specs: [
                        ...selectedRule.output_specs,
                        {
                          id: crypto.randomUUID(),
                          target_route_id:
                            connections.outgoing[0]?.route_id ?? '',
                          emit_count: 1,
                          processing_delay_seconds: 0,
                          size_multiplier: 1,
                        },
                      ],
                    })
                  }
                >
                  Add output spec
                </button>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-text-subtle lg:col-span-2">
            Add a rule to configure processing.
          </p>
        )}
      </div>

      {selectedRule && ruleExplanation && (
        <div className="rounded-md border border-border/60 bg-surface/50 px-3 py-2 text-sm text-text-muted">
          <p className="mb-1 text-xs font-medium text-text">Rule preview</p>
          <p>{ruleExplanation}</p>
        </div>
      )}

      {!readOnly && selectedRule && (
        <button
          type="button"
          className="btn-danger text-xs"
          onClick={() => {
            const next = rules.filter((r) => r.id !== selectedRule.id);
            updateRules(next);
            selectRule(next[0]?.id ?? '');
          }}
        >
          Delete selected rule
        </button>
      )}

      <NodeValidationPanel validation={combinedValidation} />
    </section>
  );
}
